import { ChangeRequest, ChangeRequestStatus } from '@app/core/model/change-request.model'
import { ScopeConfiguration, ScopeVersion } from '@app/core/model/scope-version'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { createAction, props } from '@ngrx/store'
import { ChangeRequestCreatePayload } from '../../model'
import { ChangeRequestChangesActions } from './change-request-changes.actions'
import { ChangeRequestComponentActions } from './change-request-component.actions'
import { ChangeRequestDeliverableActions } from './change-request-deliverable.actions'
import { ChangeRequestFeeActions } from './change-request-fee.actions'
import { ChangeRequestScenarioActions } from './change-request-scenario.actions'
import { ChangeRequestSectionActions } from './change-request-section.actions'

export enum ChangeRequestActionTypes {
  GET_CHANGE_REQUESTS = '[Change Request] Get Change Requests',
  GET_CHANGE_REQUESTS_SUCCESS = '[Change Request] Get Change Requests Success',
  GET_CHANGE_REQUESTS_FAIL = '[Change Request] Get Change Requests Fail',

  RESET_CURRENT_CHANGE_REQUEST = '[Change Request] Reset Current Change Request',
  RESET_STATE = '[Change Request] Reset Change Request State',
  SET_CURRENT_CHANGE_REQUEST = '[Change Request] Set Current Change Request',
  UPDATE_CURRENT_CHANGE_REQUEST_STATUS = '[Change Request] Update Current Change Request Status',

  CREATE_CHANGE_REQUEST = '[Change Request] Create Change Request',
  CREATE_CHANGE_REQUEST_SUCCESS = '[Change Request] Create Change Request Success',
  CREATE_CHANGE_REQUEST_FAIL = '[Change Request] Create Change Request Fail',

  GET_CHANGE_REQUEST = '[Change Request] Get Change Request',
  GET_CHANGE_REQUEST_SUCCESS = '[Change Request] Get Change Request Success',
  GET_CHANGE_REQUEST_FAIL = '[Change Request] Get Change Request Fail',

  GET_CHANGE_REQUEST_PREVIEW = '[Change Request] Get Change Request Preview',
  GET_CHANGE_REQUEST_PREVIEW_SUCCESS = '[Change Request] Get Change Request Preview Success',
  GET_CHANGE_REQUEST_PREVIEW_FAIL = '[Change Request] Get Change Request Preview Fail',

  CHANGE_REQUEST_ABLE_TO_MERGE = '[Change Request] Able to merge',
  CHANGE_REQUEST_ABLE_TO_MERGE_SUCCESS = '[Change Request] Able to merge success',
  CHANGE_REQUEST_ABLE_TO_MERGE_FAIL = '[Change Request] Able to merge fail',

  DISMISS_CHANGE_REQUEST = '[Change Request] Dismiss Change Request',
  DISMISS_CHANGE_REQUEST_SUCCESS = '[Change Request] Dismiss Change Request Success',
  DISMISS_CHANGE_REQUEST_FAIL = '[Change Request] Dismiss Change Request Fail',

  REVERT_CHANGE_REQUEST_TO_DRAFT = '[Change Request] Revert Change Request To Draft',
  REVERT_CHANGE_REQUEST_TO_DRAFT_SUCCESS = '[Change Request] Revert Change Request To Draft Success',
  REVERT_CHANGE_REQUEST_TO_DRAFT_FAIL = '[Change Request] Revert Change Request To Draft Fail',

  GET_NET_CHANGES = '[Change Request] Get Net Changes',
  GET_NET_CHANGES_SUCCESS = '[Change Request] Get Net Changes Success',
  GET_NET_CHANGES_FAIL = '[Change Request] Get Net Changes Fail',

  REBASE_CHANGES = '[Change Request] Rebase Changes',
  REBASE_CHANGES_CONFIRMED = '[Change Request] Rebase Changes Confirmed',
  REBASE_CHANGES_SUCCESS = '[Change Request] Rebase Changes Success',
  REBASE_CHANGES_FAIL = '[Change Request] Rebase Changes Fail',

  RECALCULATE_CONFIGURATION = '[Change Request] Recalculate configuration',
  RECALCULATE_CONFIGURATION_SUCCESS = '[Change Request] Recalculate configuration Success',
  RECALCULATE_CONFIGURATION_FAIL = '[Change Request] Recalculate configuration Fail',
}

const getChangeRequests = createAction(ChangeRequestActionTypes.GET_CHANGE_REQUESTS)
const getChangeRequestsSuccess = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUESTS_SUCCESS,
  props<{ changeRequests: ChangeRequest[] }>()
)
const getChangeRequestsFail = createAction(ChangeRequestActionTypes.GET_CHANGE_REQUESTS_FAIL, props<{ error: Error }>())

const resetCurrentChangeRequest = createAction(ChangeRequestActionTypes.RESET_CURRENT_CHANGE_REQUEST)
const resetState = createAction(ChangeRequestActionTypes.RESET_STATE)

const setCurrentChangeRequest = createAction(
  ChangeRequestActionTypes.SET_CURRENT_CHANGE_REQUEST,
  props<{ changeRequest: ChangeRequest | null }>()
)

const createChangeRequest = createAction(
  ChangeRequestActionTypes.CREATE_CHANGE_REQUEST,
  props<{ payload: ChangeRequestCreatePayload; callback: (changeRequest?: ChangeRequest) => void }>()
)
const createChangeRequestSuccess = createAction(
  ChangeRequestActionTypes.CREATE_CHANGE_REQUEST_SUCCESS,
  props<{ changeRequest: ChangeRequest }>()
)
const createChangeRequestFail = createAction(
  ChangeRequestActionTypes.CREATE_CHANGE_REQUEST_FAIL,
  props<{ error: Error }>()
)

const changeRequestAbleToMerge = createAction(
  ChangeRequestActionTypes.CHANGE_REQUEST_ABLE_TO_MERGE,
  props<{ scopeId?: number; changeRequestId: number }>()
)
const changeRequestAbleToMergeSuccess = createAction(
  ChangeRequestActionTypes.CHANGE_REQUEST_ABLE_TO_MERGE_SUCCESS,
  props<{ ableToMerge: boolean }>()
)
const changeRequestAbleToMergeFail = createAction(
  ChangeRequestActionTypes.CHANGE_REQUEST_ABLE_TO_MERGE_FAIL,
  props<{ error: Error }>()
)

const getChangeRequest = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUEST,
  props<{ scopeId?: number; changeRequestId: number }>()
)
const getChangeRequestSuccess = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUEST_SUCCESS,
  props<{ changeRequest: ChangeRequest }>()
)
const getChangeRequestFail = createAction(ChangeRequestActionTypes.GET_CHANGE_REQUEST_FAIL, props<{ error: Error }>())

const updateCurrentChangeRequestStatus = createAction(
  ChangeRequestActionTypes.UPDATE_CURRENT_CHANGE_REQUEST_STATUS,
  props<{ status: ChangeRequestStatus }>()
)

const getChangeRequestPreview = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUEST_PREVIEW,
  props<{ scopeId?: number; changeRequestId: number }>()
)
const getChangeRequestPreviewSuccess = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUEST_PREVIEW_SUCCESS,
  props<{ previewScope: ScopeVersion }>()
)
const getChangeRequestPreviewFail = createAction(
  ChangeRequestActionTypes.GET_CHANGE_REQUEST_PREVIEW_FAIL,
  props<{ error: Error }>()
)

const dismissChangeRequest = createAction(
  ChangeRequestActionTypes.DISMISS_CHANGE_REQUEST,
  props<{ scopeId?: number; changeRequestId: number; comment?: string }>()
)
const dismissChangeRequestSuccess = createAction(
  ChangeRequestActionTypes.DISMISS_CHANGE_REQUEST_SUCCESS,
  props<{ changeRequest: ChangeRequest }>()
)
const dismissChangeRequestFail = createAction(
  ChangeRequestActionTypes.DISMISS_CHANGE_REQUEST_FAIL,
  props<{ error: Error }>()
)

const revertChangeRequestToDraft = createAction(
  ChangeRequestActionTypes.REVERT_CHANGE_REQUEST_TO_DRAFT,
  props<{ scopeId?: number; changeRequestId: number; comment?: string }>()
)
const revertChangeRequestToDraftSuccess = createAction(
  ChangeRequestActionTypes.REVERT_CHANGE_REQUEST_TO_DRAFT_SUCCESS,
  props<{ changeRequest: ChangeRequest }>()
)
const revertChangeRequestToDraftFail = createAction(
  ChangeRequestActionTypes.REVERT_CHANGE_REQUEST_TO_DRAFT_FAIL,
  props<{ error: Error }>()
)

const rebaseChanges = createAction(
  ChangeRequestActionTypes.REBASE_CHANGES,
  props<{ scopeId: number; changeRequestId: number }>()
)
const rebaseChangesConfirmed = createAction(
  ChangeRequestActionTypes.REBASE_CHANGES_CONFIRMED,
  props<{ scopeId: number; changeRequestId: number }>()
)
const rebaseChangesSuccess = createAction(
  ChangeRequestActionTypes.REBASE_CHANGES_SUCCESS,
  props<{ changeRequest: ChangeRequest }>()
)
const rebaseChangesFail = createAction(ChangeRequestActionTypes.REBASE_CHANGES_FAIL, props<{ error: Error }>())

const recalculateConfiguration = createAction(
  ChangeRequestActionTypes.RECALCULATE_CONFIGURATION,
  props<{ scenario: Scenario; configuration: ScopeConfiguration; componentModifiers }>()
)
const recalculateConfigurationSuccess = createAction(
  ChangeRequestActionTypes.RECALCULATE_CONFIGURATION_SUCCESS,
  props<{ scopeId: number; changeRequestId: number }>()
)
const recalculateConfigurationFail = createAction(
  ChangeRequestActionTypes.RECALCULATE_CONFIGURATION_FAIL,
  props<{ error: Error }>()
)

export const ChangeRequestActions = {
  getChangeRequests,
  getChangeRequestsSuccess,
  getChangeRequestsFail,

  resetCurrentChangeRequest,
  resetState,
  setCurrentChangeRequest,
  updateCurrentChangeRequestStatus,

  createChangeRequest,
  createChangeRequestSuccess,
  createChangeRequestFail,

  getChangeRequest,
  getChangeRequestSuccess,
  getChangeRequestFail,

  getChangeRequestPreview,
  getChangeRequestPreviewSuccess,
  getChangeRequestPreviewFail,

  changeRequestAbleToMerge,
  changeRequestAbleToMergeSuccess,
  changeRequestAbleToMergeFail,

  dismissChangeRequest,
  dismissChangeRequestSuccess,
  dismissChangeRequestFail,

  revertChangeRequestToDraft,
  revertChangeRequestToDraftSuccess,
  revertChangeRequestToDraftFail,

  rebaseChanges,
  rebaseChangesConfirmed,
  rebaseChangesSuccess,
  rebaseChangesFail,

  recalculateConfiguration,
  recalculateConfigurationSuccess,
  recalculateConfigurationFail,

  ...ChangeRequestChangesActions,
  ...ChangeRequestDeliverableActions,
  ...ChangeRequestComponentActions,
  ...ChangeRequestSectionActions,
  ...ChangeRequestScenarioActions,
  ...ChangeRequestFeeActions,
}
